<template>
  <div class="snake-game-settings-container" :class="{ 'embedded-mode': isEmbedded }">
    <h1 v-if="!isEmbedded" class="game-title">{{ $t('Snakegame.SnakegameTitle') }}</h1>
    <div v-if="!isEmbedded" class="top-action-buttons">
      <button class="clear-button" @click="clearData">{{ $t('Snakegame.clearData') }}</button>
      <button class="start-button" @click="startGame" :disabled="!canStartGame">{{ $t('Snakegame.saveSettings') }}</button>
    </div>

    <!-- 主題名稱 -->
    <div class="setting-section">
      <div class="setting-title">{{ $t('Snakegame.topic') }}</div>
      <div class="vocabulary-counter">
        {{ topic.length }}/20
      </div>
      <input class="input-text" type="text" v-model="topic" :placeholder="$t('Snakegame.inputTopic')" maxlength="20">
    </div>

    <!-- 正確詞彙區塊 -->
    <div class="setting-section correct-section">
      <div class="setting-title">{{ $t('Snakegame.correctItems') }}</div>
      <div class="vocabulary-counter">
        {{ vocabularyList.length }}/20
      </div>
      <div class="vocabulary-input">
        <textarea v-model="vocabularyInput" :placeholder="$t('Snakegame.correctItemsPlaceholder')"
          @keydown="handleKeyDown"></textarea>
      </div>

      <div class="vocabulary-tags">
        <div v-for="(word, index) in vocabularyList" :key="index" class="vocabulary-tag">
          {{ word }}
          <span class="delete-tag" @click="removeVocabulary(index)">×</span>
        </div>
      </div>
    </div>

    <!-- 錯誤詞彙區塊 -->
    <div class="setting-section error-section">
      <div class="setting-title">{{ $t('Snakegame.errorItems') }}</div>
      <div class="vocabulary-counter">
        {{ errorVocabularyList.length }}/20
      </div>
      <div class="vocabulary-input">
        <textarea v-model="errorVocabularyInput" :placeholder="$t('Snakegame.errorItemsPlaceholder')"
          @keydown="handleErrorKeyDown"></textarea>
      </div>

      <div class="vocabulary-tags error-tags">
        <div v-for="(word, index) in errorVocabularyList" :key="index" class="vocabulary-tag error-tag">
          {{ word }}
          <span class="delete-tag" @click="removeErrorVocabulary(index)">×</span>
        </div>
      </div>
    </div>

    <!-- 難度選擇 -->
    <div class="setting-section">
      <div class="setting-title">{{ $t('Snakegame.difficulty') }}</div>
      <div class="difficulty-selector">
        <select v-model="difficulty">
          <option v-for="option in difficultyOptions" :key="option.value" :value="option.value">
            {{ option.label }}
          </option>
        </select>
      </div>
      <div class="difficulty-info">
        <div class="info-item">
          <span class="info-label">{{ $t('Snakegame.boardSize') }}:</span>
          <span class="info-value">{{ currentDifficultySettings.boardWidth }}x{{ currentDifficultySettings.boardHeight }}</span>
        </div>
        <div class="info-item">
          <span class="info-label">{{ $t('Snakegame.snakeSpeed') }}:</span>
          <span class="info-value">{{ currentDifficultySettings.snakeSpeed }}ms</span>
        </div>
        <div class="info-item">
          <span class="info-label">{{ $t('Snakegame.foodCount') }}:</span>
          <span class="info-value">{{ currentDifficultySettings.foodCount }} {{ $t('Snakegame.items') }}</span>
        </div>
      </div>
    </div>

    <!-- 遊戲時長 -->
    <div class="setting-section">
      <div class="setting-title">{{ $t('Snakegame.gameDuration') }}</div>
      <div class="duration-selector">
        <select v-model="gameDuration">
          <option v-for="option in durationOptions" :key="option" :value="option">
            {{ option }} {{ $t('Snakegame.second') }}
          </option>
        </select>
      </div>
    </div>

    <!-- 引用 HiTeach 名單選項（需校園版授權 + 版本 >= 5.5.0.0002） -->
    <div v-if="isNewPushFuncVersion && schoolVers" class="setting-section roster-section">
      <div class="roster-checkbox-wrapper">
        <label class="checkbox-container">
          <input type="checkbox" v-model="useHiTeachRoster" class="roster-checkbox" />
          <span class="checkbox-label">{{ $t('HTwbActKitHome.useHiTeachRoster') }}</span>
        </label>
      </div>
    </div>

    <!-- 操作按鈕 (僅獨立模式顯示) -->
    <div v-if="!isEmbedded" class="action-buttons">
      <button class="clear-button" @click="clearData">{{ $t('Snakegame.clearData') }}</button>
      <button class="start-button" @click="startGame" :disabled="!canStartGame">{{ $t('Snakegame.saveSettings') }}</button>
    </div>
  </div>
</template>

<script setup>
/* eslint-disable */
import { ref, computed, inject, getCurrentInstance, watch, onMounted } from 'vue';
import { useRouter } from 'vue-router';
import { useHiTeachVersion } from '@/composable/useHiTeachVersion';
import { useSchoolVersion } from '@/composable/useSchoolVersion';
import { setupUserAuth } from '@/composable/useGameMessageBridge';

const that = getCurrentInstance().proxy;
const $Message = inject("$Message");
const router = useRouter();

// HiTeach 版本管理
const { htVersion, isNewPushFuncVersion, loadVersion } = useHiTeachVersion('HTSnakeGameSetting');

// 校園版授權判斷
const { schoolVers } = useSchoolVersion();

// 通信工具類
const MessageBridge = {
    // 環境類型
    ENV: {
        WEBVIEW: 'webview',
        WEB: 'web'
    },
    // 當前環境
    currentEnv: 'webview',
    // 初始化
    init() {
        this.currentEnv = window?.chrome?.webview ? this.ENV.WEBVIEW : this.ENV.WEB;
        return this;
    },
    // 發送消息
    postMessage(data, origin = '*') {
        if (this.currentEnv === this.ENV.WEBVIEW) {
            window?.chrome?.webview.postMessage(data);
        } else {
            window.postMessage(data, origin);
        }
    },
    // 添加消息監聽器
    addEventListener(callback) {
        if (this.currentEnv === this.ENV.WEBVIEW) {
            window?.chrome?.webview.addEventListener('message', event => {
                callback(event);
            });
        } else {
            window.addEventListener('message', event => {
                callback(event);
            });
        }
    }
};

// 初始化消息橋接器
const messageBridge = MessageBridge.init();

// Props
const props = defineProps({
  modelValue: {
    type: Object,
    default: () => ({})
  },
  activityName: {
    type: String,
    default: ''
  },
  playerCount: {
    type: Number,
    default: 1
  },
  isEmbedded: {
    type: Boolean,
    default: false
  }
});

// Emits
const emit = defineEmits(['update:modelValue']);

// 難度設定映射（長方形棋盤：寬 x 高）
const difficultySettings = {
  easy: {
    boardWidth: 20,
    boardHeight: 12,
    snakeSpeed: 300,
    foodCount: 6
  },
  medium: {
    boardWidth: 25,
    boardHeight: 15,
    snakeSpeed: 200,
    foodCount: 8
  },
  hard: {
    boardWidth: 30,
    boardHeight: 18,
    snakeSpeed: 150,
    foodCount: 10
  }
};

// 表單資料
const topic = ref('');
const vocabularyInput = ref('');
const vocabularyList = ref([]);
const errorVocabularyInput = ref('');
const errorVocabularyList = ref([]);
const difficulty = ref('medium');
const gameDuration = ref(60);
const originalConfig = ref(null); // 記錄載入時的原始設定（用於比對是否有變動）

// HiTeach 名單選項
const useHiTeachRoster = ref(false);

// 難度選項
const difficultyOptions = computed(() => [
  { value: 'easy', label: that.$t('Snakegame.difficultyEasy') },
  { value: 'medium', label: that.$t('Snakegame.difficultyMedium') },
  { value: 'hard', label: that.$t('Snakegame.difficultyHard') }
]);

// 時長選項
const durationOptions = [60, 90, 120];

// 當前難度設定
const currentDifficultySettings = computed(() => difficultySettings[difficulty.value]);

// 監聽難度變化，自動調整參數
watch(difficulty, () => {
  emitUpdate();
});

// 計算是否可以開始遊戲（與打地鼠一致：至少 1 個詞彙即可）
const canStartGame = computed(() =>
  vocabularyList.value.length > 0 &&
  errorVocabularyList.value.length > 0
);

// 初始化：如果有 modelValue，載入資料
if (props.modelValue && Object.keys(props.modelValue).length > 0) {
  topic.value = props.modelValue.topic || '';
  vocabularyList.value = props.modelValue.vocabularyList || [];
  errorVocabularyList.value = props.modelValue.errorVocabularyList || [];
  difficulty.value = props.modelValue.difficulty || 'medium';
  gameDuration.value = props.modelValue.gameDuration || 60;
}

// 處理正確詞彙的按鍵事件
const handleKeyDown = (event) => {
  // 移除紅框 (如果使用者開始編輯)
  const textarea = event.target;
  if (textarea.style.border === '2px solid red') {
    textarea.style.border = '';
  }

  if (event.key === 'Enter') {
    event.preventDefault();
    addVocabulary();
  }
};

// 處理錯誤詞彙的按鍵事件
const handleErrorKeyDown = (event) => {
  // 移除紅框 (如果使用者開始編輯)
  const textarea = event.target;
  if (textarea.style.border === '2px solid red') {
    textarea.style.border = '';
  }

  if (event.key === 'Enter') {
    event.preventDefault();
    addErrorVocabulary();
  }
};

// 新增正確詞彙
const addVocabulary = () => {
  const words = vocabularyInput.value.split(/[,，\n\r]+/).filter(word => word.trim() !== '');

  for (const word of words) {
    if (vocabularyList.value.includes(word.trim())) {
      $Message.warning({
        content: that.$t('Snakegame.wordExists'),
        timeout: 2000,
      });
      continue;
    }
    if (vocabularyList.value.length < 20 && !vocabularyList.value.includes(word.trim())) {
      vocabularyList.value.push(word.trim());
    }
  }

  if (vocabularyList.value.length === 20) {
    $Message.warning({
      content: that.$t('Snakegame.maxVocabulary'),
      timeout: 2000,
    });
  }

  vocabularyInput.value = '';
  emitUpdate();
};

// 新增錯誤詞彙
const addErrorVocabulary = () => {
  const words = errorVocabularyInput.value.split(/[,，\n\r]+/).filter(word => word.trim() !== '');

  for (const word of words) {
    if (errorVocabularyList.value.includes(word.trim())) {
      $Message.warning({
        content: that.$t('Snakegame.wordExists'),
        timeout: 2000,
      });
      continue;
    }
    if (errorVocabularyList.value.length < 20 && !errorVocabularyList.value.includes(word.trim())) {
      errorVocabularyList.value.push(word.trim());
    }
  }

  if (errorVocabularyList.value.length === 20) {
    $Message.warning({
      content: that.$t('Snakegame.maxErrorVocabulary'),
      timeout: 2000,
    });
  }

  errorVocabularyInput.value = '';
  emitUpdate();
};

// 移除正確詞彙
const removeVocabulary = (index) => {
  vocabularyList.value.splice(index, 1);
  emitUpdate();
};

// 移除錯誤詞彙
const removeErrorVocabulary = (index) => {
  errorVocabularyList.value.splice(index, 1);
  emitUpdate();
};

// 清空設定數據
const clearData = () => {
  topic.value = '';
  vocabularyList.value = [];
  vocabularyInput.value = '';
  errorVocabularyList.value = [];
  errorVocabularyInput.value = '';
  difficulty.value = 'medium';
  gameDuration.value = 60;

  // 清除 sessionStorage 中的配置
  sessionStorage.removeItem('SnakeGameConfig');

  emitUpdate();

  $Message.success({
    content: that.$t('Snakegame.dataCleared'),
    timeout: 2000,
  });
};

// 向父元件發送更新
const emitUpdate = () => {
  const settings = currentDifficultySettings.value;

  const updateData = {
    topic: topic.value,
    vocabularyList: [...vocabularyList.value],
    errorVocabularyList: [...errorVocabularyList.value],
    difficulty: difficulty.value,
    gameDuration: gameDuration.value,
    boardWidth: settings.boardWidth,
    boardHeight: settings.boardHeight,
    snakeSpeed: settings.snakeSpeed,
    foodCount: settings.foodCount,
    initialSnakeLength: 3
  };

  console.log('🐍 [HTSnakeGameSetting] emitUpdate:', {
    difficulty: updateData.difficulty,
    boardWidth: updateData.boardWidth,
    boardHeight: updateData.boardHeight,
    snakeSpeed: updateData.snakeSpeed,
    foodCount: updateData.foodCount
  });

  emit('update:modelValue', updateData);
};

// 監聽所有變化,自動更新
watch([topic, vocabularyList, errorVocabularyList, gameDuration, difficulty], () => {
  emitUpdate();
}, { deep: true });

// 驗證輸入框是否有未提交的內容
const validateInputs = () => {
  const hasUnsubmittedCorrect = vocabularyInput.value.trim() !== '';
  const hasUnsubmittedError = errorVocabularyInput.value.trim() !== '';

  if (hasUnsubmittedCorrect || hasUnsubmittedError) {
    // 為有內容的輸入框添加紅框
    const correctTextarea = document.querySelector('.correct-section textarea');
    const errorTextarea = document.querySelector('.error-section textarea');

    if (hasUnsubmittedCorrect && correctTextarea) {
      correctTextarea.style.border = '2px solid red';
    }
    if (hasUnsubmittedError && errorTextarea) {
      errorTextarea.style.border = '2px solid red';
    }

    // 顯示提示訊息
    $Message.warning({
      content: that.$t('Snakegame.unsubmittedWarning'),
      timeout: 3000,
    });

    return false; // 驗證失敗
  }

  return true; // 驗證成功
};

// 暴露驗證方法和清空方法給父組件 (嵌入模式使用)
defineExpose({
  validateInputs,
  clearData
});

// 儲存設定並跳轉（獨立模式使用）
const startGame = () => {
  // 驗證輸入框
  if (!validateInputs()) {
    return; // 驗證失敗，不執行儲存
  }

  if (!canStartGame.value) {
    $Message.warning({
      content: that.$t('Snakegame.pleaseCompleteSettings'),
      timeout: 2000,
    });
    return;
  }

  const settings = currentDifficultySettings.value;

  const gameSettings = {
    topic: topic.value,
    correctVocabularyList: JSON.parse(JSON.stringify(vocabularyList.value)),
    errorVocabularyList: JSON.parse(JSON.stringify(errorVocabularyList.value)),
    difficulty: difficulty.value,
    gameDuration: gameDuration.value,
    boardWidth: settings.boardWidth,
    boardHeight: settings.boardHeight,
    snakeSpeed: settings.snakeSpeed,
    foodCount: settings.foodCount,
    initialSnakeLength: 3,
    useHiTeachRoster: useHiTeachRoster.value
  };

  console.log('🐍 貪食蛇遊戲設定:', gameSettings);

  // 比對設定是否有變動（僅在有過去推送時才需比對）
  if (originalConfig.value?.restoredData) {
    const orig = originalConfig.value;
    const origComp = JSON.stringify({ topic: orig.topic, correctVocabularyList: orig.correctVocabularyList, errorVocabularyList: orig.errorVocabularyList, difficulty: orig.difficulty, gameDuration: orig.gameDuration, useHiTeachRoster: orig.useHiTeachRoster });
    const currComp = JSON.stringify({ topic: gameSettings.topic, correctVocabularyList: gameSettings.correctVocabularyList, errorVocabularyList: gameSettings.errorVocabularyList, difficulty: gameSettings.difficulty, gameDuration: gameSettings.gameDuration, useHiTeachRoster: gameSettings.useHiTeachRoster });
    if (origComp === currComp) {
      // 設定未變動：保留 restoredData，不需重新推送
      $Message.warning({ content: that.$t('Game.settingsUnchanged'), timeout: 3000 });
      gameSettings.restoredData = orig.restoredData;
    }
  }

  // 立即寫入 sessionStorage，確保 play 頁讀到最新值（含 useHiTeachRoster）
  sessionStorage.setItem('SnakeGameConfig', JSON.stringify(gameSettings));

  // 發送協議（與打地鼠模式一致）
  messageBridge.postMessage({
    action: 'Save.SnakeGameSetting',
    data: gameSettings
  });

  // 導航到遊戲頁面
  router.push('/ht-snakegame-play');
};

// onMounted 處理從 sessionStorage 載入配置
onMounted(() => {
  messageBridge.addEventListener(event => {
    const msg = event.data;
    if (msg.action === 'Init.GameHome') {
      setupUserAuth(msg.data, 'HTSnakeGameSetting');
    }
  });

  // 載入 HiTeach 版本號
  loadVersion();

  let rosterFromConfig = false;

  // 從 sessionStorage 讀取配置資料
  const savedConfig = sessionStorage.getItem('SnakeGameConfig');
  if (savedConfig) {
    try {
      const config = JSON.parse(savedConfig);
      originalConfig.value = config; // 記錄原始設定
      console.log('🐍 從 sessionStorage 讀取到的配置:', config);

      // 填充表單欄位
      if (config.topic) topic.value = config.topic;
      if (config.correctVocabularyList && Array.isArray(config.correctVocabularyList)) {
        vocabularyList.value = [...config.correctVocabularyList];
      }
      if (config.errorVocabularyList && Array.isArray(config.errorVocabularyList)) {
        errorVocabularyList.value = [...config.errorVocabularyList];
      }
      if (config.gameDuration) gameDuration.value = config.gameDuration;
      if (config.difficulty) difficulty.value = config.difficulty;
      if (typeof config.useHiTeachRoster !== 'undefined') {
        useHiTeachRoster.value = !!config.useHiTeachRoster;
        rosterFromConfig = true;
      }

    } catch (error) {
      console.error('🐍 解析 sessionStorage 中的配置時出錯:', error);
    }
  }

  // 校園版且未從配置載入名單偏好 → 預設勾選
  if (!rosterFromConfig && schoolVers.value) {
    useHiTeachRoster.value = true;
  }
});
</script>

<style lang="less" scoped>
.snake-game-settings-container {
  max-width: 800px;
  margin: 0 auto;
  padding: 10px 0px 20px 0px;

  // 嵌入模式樣式（在 GameParametersForm 中使用）
  &.embedded-mode {
    max-width: 100%;
    padding: 0;
    margin: 0;
  }
}

.top-action-buttons {
  display: flex;
  justify-content: flex-end;
  gap: 8px;
  margin-bottom: 8px;

  .clear-button {
    background-color: #f44336;
    color: white;
    border: none;
    border-radius: 4px;
    padding: 5px 10px;
    font-size: 1rem;
    cursor: pointer;
    font-family: inherit;
    &:hover { background-color: #da190b; }
  }

  .start-button {
    background-color: #4caf50;
    color: white;
    border: none;
    border-radius: 4px;
    padding: 5px 10px;
    font-size: 1rem;
    cursor: pointer;
    font-family: inherit;
    &:hover { background-color: #45a049; }
    &:disabled { background-color: #cccccc; cursor: not-allowed; }
  }
}

.game-title {
  font-size: 2rem;
  text-align: center;
  margin-bottom: 20px;
}

.setting-section {
  position: relative;
  margin-bottom: 16px;
  background-color: #f5f5f5;
  border-radius: 8px;
  padding: 16px;

  .setting-title {
    font-size: 16px;
    font-weight: 600;
    margin-bottom: 10px;
    line-height: 1.6;
  }

  .input-text {
    width: 100%;
    height: 40px;
    padding: 10px;
    border: 1px solid #ddd;
    border-radius: 4px;
    font-family: inherit;
    font-size: 14px;
    line-height: 1.6;
  }

  &.correct-section {
    background-color: #e8f4ff;

    .setting-title {
      color: blue;
    }
  }

  &.error-section {
    background-color: #fff0f3;

    .setting-title {
      color: #d81b60;
    }
  }
}

.vocabulary-input {
  textarea {
    width: 100%;
    height: 80px;
    padding: 10px;
    border: 1px solid #ddd;
    border-radius: 4px;
    resize: vertical;
    font-family: inherit;
    font-size: 14px;
    line-height: 1.6;
    margin-bottom: 10px;
  }
}

.vocabulary-tags {
  display: flex;
  flex-wrap: wrap;
  gap: 8px;

  .vocabulary-tag {
    background-color: #e8f4ff;
    border: 1px solid #b8dcff;
    border-radius: 20px;
    padding: 4px 10px;
    display: flex;
    align-items: center;
    font-size: 14px;
    line-height: 1.6;

    .delete-tag {
      margin-left: 5px;
      cursor: pointer;
      font-weight: bold;
      color: #666;

      &:hover {
        color: #ff4d4d;
      }
    }

    &.error-tag {
      background-color: #ffe0e9;
      border: 1px solid #ffb6c9;

      .delete-tag {
        &:hover {
          color: #d81b60;
        }
      }
    }
  }
}

.vocabulary-counter {
  position: absolute;
  right: 18px;
  top: 18px;
  font-size: 14px;
  color: #666;
  font-weight: bold;
}

.difficulty-selector,
.duration-selector {
  select {
    width: 100%;
    padding: 10px;
    border: 1px solid #ddd;
    border-radius: 4px;
    font-family: inherit;
    font-size: 14px;
    line-height: 1.6;
  }
}

.difficulty-info {
  margin-top: 15px;
  padding: 15px;
  background-color: #fff;
  border-radius: 4px;
  border: 1px solid #ddd;

  .info-item {
    display: flex;
    justify-content: space-between;
    align-items: center;
    margin-bottom: 8px;
    font-size: 0.95rem;

    &:last-child {
      margin-bottom: 0;
    }

    .info-label {
      color: #666;
      font-weight: 500;
    }

    .info-value {
      color: #333;
      font-weight: 600;
    }
  }
}

// 名單選項區塊
.roster-section {
  .roster-checkbox-wrapper {
    display: flex;
    align-items: center;
  }

  .checkbox-container {
    display: flex;
    align-items: center;
    cursor: pointer;
    user-select: none;

    .roster-checkbox {
      width: 20px;
      height: 20px;
      cursor: pointer;
      margin-right: 10px;
    }

    .checkbox-label {
      font-size: 1.2rem;
    }
  }
}

.action-buttons {
  display: flex;
  justify-content: center;
  gap: 15px;
  margin-top: 20px;

  .clear-button {
    background-color: #f44336;
    color: white;
    border: none;
    border-radius: 4px;
    padding: 10px 20px;
    font-size: 1.1rem;
    cursor: pointer;
    font-family: inherit;
    transition: background-color 0.2s;

    &:hover {
      background-color: #da190b;
    }
  }

  .start-button {
    background-color: #4caf50;
    color: white;
    border: none;
    border-radius: 4px;
    padding: 10px 20px;
    font-size: 1.1rem;
    cursor: pointer;
    font-family: inherit;
    transition: background-color 0.2s;

    &:hover {
      background-color: #45a049;
    }

    &:disabled {
      background-color: #cccccc;
      cursor: not-allowed;
    }
  }
}
</style>
